import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";

type RamsForm = {
  company: string;
  project: string;
  site: string;
  supervisor: string;
  assessor: string;
  date: string;
  reviewDate: string;
  scope: string;
  sequence: string;
  plantTools: string;
  materials: string;
  permits: string;
  interfaces: string;
  emergency: string;
  environmental: string;
  notes: string;
  hotWorks: boolean;
  workingAtHeight: boolean;
  liftingOps: boolean;
  excavations: boolean;
  electricalIsolation: boolean;
  test: boolean;
};

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const LS_KEY = "lumia:rams:v3";

export default function Rams() {
  const [form, setForm] = useState<RamsForm>(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) return JSON.parse(saved) as RamsForm;
    } catch {
      // ignore bad localStorage
    }

    return {
      company: "",
      project: "",
      site: "",
      supervisor: "",
      assessor: "",
      date: todayISO(),
      reviewDate: "",
      scope: "",
      sequence: "",
      plantTools: "",
      materials: "",
      permits: "",
      interfaces: "",
      emergency: "",
      environmental: "",
      notes: "",
      hotWorks: false,
      workingAtHeight: false,
      liftingOps: false,
      excavations: false,
      electricalIsolation: false,
      test: true,
    };
  });

  const [draft, setDraft] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  function update<K extends keyof RamsForm>(key: K, value: RamsForm[K]) {
    const next = { ...form, [key]: value };
    setForm(next);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
  }

  const canSubmit = useMemo(() => {
    return !!(
      form.company.trim() &&
      form.project.trim() &&
      form.site.trim() &&
      form.supervisor.trim() &&
      form.assessor.trim() &&
      form.date.trim() &&
      form.reviewDate.trim() &&
      (form.scope.trim() || form.notes.trim())
    );
  }, [form]);

  async function generate() {
    setErrMsg("");
    setDraft("");
    setIsLoading(true);

    try {
      const res = await fetch("/api/rams", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "RAMS generation failed.");
      }

      setDraft(String(data?.draft || ""));
    } catch (e: any) {
      setErrMsg(e?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  }

  async function copyDraft() {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      setErrMsg("Couldn’t copy to clipboard.");
    }
  }

  async function downloadPdf() {
    if (!draft) return;

    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const left = 15;
      const right = pageWidth - 15;
      const maxWidth = pageWidth - 30;

      function footer(pageNo: number) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text("Lumia RAMS", left, pageHeight - 8);
        doc.text(`Page ${pageNo}`, right, pageHeight - 8, { align: "right" });
      }

      function ensureSpace(y: number, needed: number) {
        if (y + needed > pageHeight - 20) {
          footer(doc.getNumberOfPages());
          doc.addPage();
          return 20;
        }
        return y;
      }

      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("RISK ASSESSMENT & METHOD STATEMENT (RAMS)", left, 24);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Prepared by Lumia", left, 32);

      doc.setLineWidth(0.4);
      doc.line(left, 36, right, 36);

      let y = 46;

      const titleRows: Array<[string, string]> = [
        ["Company", form.company || "-"],
        ["Project", form.project || "-"],
        ["Site", form.site || "-"],
        ["Supervisor", form.supervisor || "-"],
        ["Assessor", form.assessor || "-"],
        ["Date", form.date || "-"],
        ["Review date", form.reviewDate || "-"],
      ];

      doc.setFontSize(11);
      for (const [label, value] of titleRows) {
        doc.setFont("helvetica", "bold");
        doc.text(`${label}:`, left, y);
        doc.setFont("helvetica", "normal");

        const wrapped = doc.splitTextToSize(String(value), maxWidth - 34);
        doc.text(wrapped, left + 34, y);

        y += Math.max(7, wrapped.length * 6);
      }

      const selectedFlags = [
        form.hotWorks ? "Hot works" : null,
        form.workingAtHeight ? "Working at height" : null,
        form.liftingOps ? "Lifting operations" : null,
        form.excavations ? "Excavations / groundworks" : null,
        form.electricalIsolation ? "Electrical isolation / live services" : null,
      ].filter(Boolean) as string[];

      y += 2;
      doc.setFont("helvetica", "bold");
      doc.text("High-risk flags:", left, y);
      doc.setFont("helvetica", "normal");

      const flagsText = selectedFlags.length ? selectedFlags.join(", ") : "None indicated";
      const wrappedFlags = doc.splitTextToSize(flagsText, maxWidth - 34);
      doc.text(wrappedFlags, left + 34, y);
      y += Math.max(8, wrappedFlags.length * 6);

      y += 4;
      doc.setLineWidth(0.2);
      doc.line(left, y, right, y);
      y += 10;

      if (form.scope?.trim()) {
        y = ensureSpace(y, 24);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text("Scope summary", left, y);
        y += 8;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        const scopeLines = doc.splitTextToSize(form.scope, maxWidth);

        for (const line of scopeLines) {
          y = ensureSpace(y, 7);
          doc.text(line, left, y);
          y += 6;
        }

        y += 6;
        doc.setLineWidth(0.2);
        doc.line(left, y, right, y);
        y += 10;
      }

      doc.setFontSize(11);
      const lines = draft.split("\n");

      for (const rawLine of lines) {
        const line = rawLine.trim();

        if (line.startsWith("# ")) {
          y = ensureSpace(y, 14);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.text(line.replace(/^# /, ""), left, y);
          y += 10;
          continue;
        }

        if (line.startsWith("## ")) {
          y = ensureSpace(y, 12);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(13);
          doc.text(line.replace(/^## /, ""), left, y);
          y += 8;
          continue;
        }

        if (line.startsWith("### ")) {
          y = ensureSpace(y, 10);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11.5);
          doc.text(line.replace(/^### /, ""), left, y);
          y += 7;
          continue;
        }

        if (line === "---") {
          y = ensureSpace(y, 8);
          doc.setLineWidth(0.2);
          doc.line(left, y, right, y);
          y += 8;
          continue;
        }

        if (!line) {
          y += 4;
          continue;
        }

        if (line.startsWith("- ")) {
          const bulletText = line.replace(/^- /, "");
          const wrapped = doc.splitTextToSize(bulletText, maxWidth - 6);
          y = ensureSpace(y, wrapped.length * 6 + 2);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(10.5);
          doc.text("•", left, y);
          doc.text(wrapped, left + 5, y);
          y += wrapped.length * 6 + 1;
          continue;
        }

        const wrapped = doc.splitTextToSize(rawLine, maxWidth);
        y = ensureSpace(y, wrapped.length * 6 + 2);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        doc.text(wrapped, left, y);
        y += wrapped.length * 6 + 1;
      }

      footer(doc.getNumberOfPages());
      doc.save(`rams-${(form.project || "project").replace(/\s+/g, "-")}.pdf`);
    } catch {
      setErrMsg("PDF generation failed.");
    }
  }



  async function downloadDocx() {
    if (!draft) return;

    try {
      const { Document, Packer, Paragraph } = await import("docx");

      const selectedFlags = [
        form.hotWorks ? "Hot works" : null,
        form.workingAtHeight ? "Working at height" : null,
        form.liftingOps ? "Lifting operations" : null,
        form.excavations ? "Excavations / groundworks" : null,
        form.electricalIsolation ? "Electrical isolation / live services" : null,
      ].filter(Boolean) as string[];

      const headerLines = [
        "RISK ASSESSMENT & METHOD STATEMENT (RAMS)",
        "",
        `Company: ${form.company || "-"}`,
        `Project: ${form.project || "-"}`,
        `Site: ${form.site || "-"}`,
        `Supervisor: ${form.supervisor || "-"}`,
        `Assessor: ${form.assessor || "-"}`,
        `Date: ${form.date || "-"}`,
        `Review Date: ${form.reviewDate || "-"}`,
        `High-risk flags: ${selectedFlags.length ? selectedFlags.join(", ") : "None indicated"}`,
        "",
        form.scope?.trim() ? "Scope summary" : "",
        form.scope?.trim() ? form.scope.trim() : "",
        form.scope?.trim() ? "" : "",
      ].filter((line, index, arr) => line !== "" || arr[index - 1] !== "");

      const doc = new Document({
        sections: [
          {
            children: [
              ...headerLines.map((line) => new Paragraph(line)),
              ...draft.split("\n").map((line) => new Paragraph(line)),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rams-${(form.project || "project").replace(/\s+/g, "-")}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setErrMsg("DOCX generation failed. Make sure the docx package is installed.");
    }
  }  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "24px 16px" }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>RAMS Generator</h1>
      <p style={{ marginTop: 0, opacity: 0.85 }}>
        Generate professional RAMS documents from one set of site inputs.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
          marginTop: 16,
        }}
      >
        <label>
          Company
          <input
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>

        <label>
          Project
          <input
            value={form.project}
            onChange={(e) => update("project", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>

        <label>
          Site
          <input
            value={form.site}
            onChange={(e) => update("site", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>

        <label>
          Supervisor / person in charge
          <input
            value={form.supervisor}
            onChange={(e) => update("supervisor", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>

        <label>
          Assessor
          <input
            value={form.assessor}
            onChange={(e) => update("assessor", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>

        <label>
          Date
          <input
            type="date"
            value={form.date}
            onChange={(e) => update("date", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>

        <label>
          Review date
          <input
            type="date"
            value={form.reviewDate}
            onChange={(e) => update("reviewDate", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 12,
          marginTop: 12,
        }}
      >
        <label>
          Scope of works
          <textarea
            value={form.scope}
            onChange={(e) => update("scope", e.target.value)}
            style={{ width: "100%", minHeight: 110 }}
          />
        </label>

        <label>
          Sequence of works
          <textarea
            value={form.sequence}
            onChange={(e) => update("sequence", e.target.value)}
            style={{ width: "100%", minHeight: 110 }}
          />
        </label>

        <label>
          Plant / tools
          <textarea
            value={form.plantTools}
            onChange={(e) => update("plantTools", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
          />
        </label>

        <label>
          Materials / substances
          <textarea
            value={form.materials}
            onChange={(e) => update("materials", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
          />
        </label>

        <label>
          Permits / authorisations
          <textarea
            value={form.permits}
            onChange={(e) => update("permits", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
          />
        </label>

        <label>
          Interfaces / public / other trades
          <textarea
            value={form.interfaces}
            onChange={(e) => update("interfaces", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
          />
        </label>

        <label>
          Emergency arrangements
          <textarea
            value={form.emergency}
            onChange={(e) => update("emergency", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
          />
        </label>

        <label>
          Environmental controls
          <textarea
            value={form.environmental}
            onChange={(e) => update("environmental", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
          />
        </label>
      </div>

      <label style={{ display: "block", marginTop: 12 }}>
        Additional notes
        <textarea
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
          style={{ width: "100%", minHeight: 120 }}
        />
      </label>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12 }}>
        <label>
          <input
            type="checkbox"
            checked={form.hotWorks}
            onChange={(e) => update("hotWorks", e.target.checked)}
          />{" "}
          Hot works
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.workingAtHeight}
            onChange={(e) => update("workingAtHeight", e.target.checked)}
          />{" "}
          Working at height
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.liftingOps}
            onChange={(e) => update("liftingOps", e.target.checked)}
          />{" "}
          Lifting operations
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.excavations}
            onChange={(e) => update("excavations", e.target.checked)}
          />{" "}
          Excavations / groundworks
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.electricalIsolation}
            onChange={(e) => update("electricalIsolation", e.target.checked)}
          />{" "}
          Electrical isolation / live services
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.test}
            onChange={(e) => update("test", e.target.checked)}
          />{" "}
          Test mode
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button onClick={generate} disabled={!canSubmit || isLoading}>
          {isLoading ? "Generating…" : "Generate RAMS"}
        </button>
        <button onClick={copyDraft} disabled={!draft}>
          Copy to Word
        </button>
        <button onClick={downloadPdf} disabled={!draft}>
          Download PDF
        </button>
        <button onClick={downloadDocx} disabled={!draft}>
          Download DOCX
        </button>
      </div>

      <p style={{ marginTop: 10, opacity: 0.75 }}>
        Generates one combined RAMS draft in Markdown, ready to review, paste into Word, or export as PDF/DOCX.
      </p>

      {errMsg ? (
        <div
          style={{
            marginTop: 12,
            color: "crimson",
            border: "1px solid rgba(220,20,60,0.25)",
            padding: 12,
            borderRadius: 8,
            background: "rgba(220,20,60,0.05)",
          }}
        >
          {errMsg}
        </div>
      ) : null}

      {draft ? (
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 10,
            padding: 16,
            marginTop: 16,
          }}
        >
          <ReactMarkdown>{draft}</ReactMarkdown>
        </div>
      ) : null}
    </div>
  );
}